/**
 * Widget execution — the data a widget renders (CT-5183, rev4 CT-5641).
 *
 * `POST /v0/dashboard/widget/execute` runs the widget's stored definition.
 * Since the rev4 engine landed on dev (2026-10-20) the body is **already
 * shaped for the widget's own type**: the backend runs the shaper and answers
 * `data` in the form that type's `output_schema` declares — a table's `rows`
 * and `columns`, a KPI's `value` and `comparisons`, a chart's `series`. Guide
 * 11 §9 puts it plainly: *"Backend already shapes body.shape/status/data/page/
 * error; frontend does not run the shaper again."*
 *
 * So this file no longer interprets anything. What it used to do — reconcile
 * three result grains, map `GROUPER`/`AGGREGATION` back to the contract's own
 * role names, and re-key rows through six heuristic passes because the
 * executor named its columns one way and its rows another — is gone with the
 * response that needed it.
 *
 * ## The shape, read once
 *
 * | Key | What it says |
 * |---|---|
 * | `shape` | `SCALAR` (KPI), `COLLECTION` (tables, charts), `ONE` (profile card) |
 * | `status` | `READY` when there is data to draw; `EMPTY` when the query answered with none |
 * | `data` | The type's own shape. Its keys are **not** camelised |
 * | `page` | `{ page, pageSize, totalCount, hasNext }` |
 * | `outputContract` | `{ typeCode, typeVersion, revision }` — which type `data` was shaped for |
 *
 * **A null value is an absent key.** The shaper omits it rather than writing
 * `null`, so every reader of `values` has to tolerate a missing one. The
 * `@aimsos/ui` widgets already do.
 */

import { validateApiResponse } from '../shared/utils/apiResponse.js';
import { apiDashboards, WIDGET_API_PREFIX } from './apiClient.js';

/** `body.shape` — how many rows `data` describes. */
export const RESULT_SHAPES = Object.freeze(['SCALAR', 'COLLECTION', 'ONE']);

/** `body.status` — only `READY` carries data. */
export const RESULT_STATUS_READY = 'READY';

/**
 * The widget ran and there is nothing to draw.
 *
 * A `SUM` over rows that are all null, a filter that matches nothing: the
 * call succeeds, `data` comes back without its value, and the widget has to
 * say so. It is not the same as "no data loaded yet", and the difference
 * matters because an unloaded widget draws its **sample** — so treating
 * `EMPTY` as unloaded would answer a real empty result with invented
 * numbers.
 */
export const RESULT_STATUS_EMPTY = 'EMPTY';

/**
 * Result → what the hooks pass around.
 *
 * A rename and nothing more: `data` is handed to the widget untouched,
 * because the only component that knows how to read a `FUNNEL`'s stages is
 * the one that draws them.
 */
export const mapApiWidgetResultToUi = (raw = {}) => ({
  id: raw.widgetUuid ?? '',
  versionId: raw.widgetVersionUuid ?? '',
  shape: raw.shape ?? '',
  status: raw.status ?? '',
  /** The type's own shape, verbatim. */
  data: raw.data ?? null,
  page: raw.page ?? null,
  /** Set when the engine could run the widget but not answer with data. */
  error: raw.error ?? null,
  /** Which type and revision `data` was shaped for. */
  outputContract: raw.outputContract ?? null,
});

/**
 * Run a widget and get its data.
 *
 * **A saved widget is the only thing it can run.** The endpoint executes the
 * stored definition; there is no inline definition to preview an unsaved
 * configuration with. That is why the builder shows sample data until the
 * first save and whenever the form is dirty.
 *
 * Errors arrive as the usual envelope and reach the caller as a throw
 * carrying the backend's own wording:
 *
 * - `DSW014` (400) — malformed `widgetUuid`, or a `recordUuid` that is not one.
 * - `DSW001` (400) — the engine rejected a parameter; a required one missing,
 *   for instance.
 * - `DSW010` (404) — the widget is not visible in this workspace.
 * - `DSW028` (422) — the widget cannot be executed as defined: no published
 *   version, type not active.
 * - `DSW029` (502) — reading the data failed inside the engine.
 *
 * @param {string} widgetUuid
 * @param {{ parameters?: Record<string, unknown>, recordUuid?: string }} [options]
 *   `parameters` are the user-filter values by `parameterCode`, plus `page`
 *   and `pageSize`. `recordUuid` is the open record, for the context types
 *   (profile card and the like) — it travels at the top level and **never**
 *   inside `parameters`, which the engine rejects.
 */
export async function executeWidget(widgetUuid, { parameters, recordUuid } = {}) {
  const response = await apiDashboards.post(`${WIDGET_API_PREFIX}/execute`, {
    widgetUuid,
    ...(parameters ? { parameters } : {}),
    ...(recordUuid ? { recordUuid } : {}),
  });
  const { body } = validateApiResponse(response, 'Could not load the widget data.');
  return mapApiWidgetResultToUi(body ?? {});
}
